<template>
  <ModalDialog
    ref="dialog"
    title="Revert Panel"
    icon="fa-undo"
    :showFooter="true"
    :scrollable="true"
    :submitDisabled="taskRevertCount == 0 || tasksHaveErrors()"
    :submitting="submitting"
    :submitTitle="submitTitle"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />

    <div v-if="!loading && productionPanelData && isRevertAvailable">
      <FormGroup v-bind:form="form" :labelColumnSize="3">
        <FormRow label="Panel Details">
          <FormStaticLabel>
            {{productionPanelData.barcode}}<br />
            {{productionPanelData.orderItem.stock.code}} - {{productionPanelData.orderItem.stock.name}}<br />
            {{productionPanelData.panelHeight}} x {{productionPanelData.panelWidth}}<br />
            Order: #{{productionPanelData.order.sequenceNumber}}
          </FormStaticLabel>
        </FormRow>
        <FormHorizontalRule />
        <h2>Task List</h2>
        <div class="row">
          <div class="col-12">
            <div class="p-0">
              <StationProgressTrackerItem
                v-for="stageTask of taskList"
                :key="stageTask.id"
                :title="stageTask.productionStage.name"
                :completed="stageTask.status == ProductionPanelStageTaskStatusTypes.completed"
                :current="isTaskCurrent(taskList, stageTask)"
                :highlighted="stageTask.selected"
                :dueDate="stageTask.dueDate"
                :completedDate="stageTask.completionDate">

                <div v-if="stageTask.editable" class="task-editable-area">
                  <div class="task-editable-item">
                    <div class="row no-gutters">
                      <div class="col-6">
                        <div v-if="isTaskRevertedAhead(stageTask)">
                          <Icon iconStyle="fas" icon="fa-exclamation-circle" iconClass="text-danger" iconStack="fa-circle" iconStackClass="text-white" class="mr-1"></Icon>
                          Tasks must be reverted in previous stages
                        </div>
                      </div>
                      <div class="col-6 text-right">
                        <strong>REVERT</strong>
                        <FormSwitch v-model="stageTask.selected" booleanMode />
                      </div>
                    </div>                    
                  </div>
                </div>
              </StationProgressTrackerItem>
            </div>
          </div>
        </div>
      </FormGroup>
    </div>
  </ModalDialog>
</template>

<style scoped>
.task-editable-area {
  padding-top: 0.5rem;
  padding-left: 2rem;
  padding-right: 2rem;
  font-size: 0.9rem;
}
.task-editable-title {
  font-size: 0.95rem;
}
.task-editable-item {
  padding-left: 0.5rem;
  font-size: 0.9rem;
}
</style>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import StationProgressTrackerItem from '@/apps/glass-production-station/src/components/schedule/stationProgressTrackerItem'

import { ProductionPanelStageTaskStatusTypes } from '@/store/types'

export default {
  name: 'RevertPanelDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    StationProgressTrackerItem
  },
  props: {
    productionStationId: Number,
    productionPanelId: Number
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      revertPanelOptions: { },
      taskList: [],
      productionPanelData: null,
      form: {},
      submitting: false
    }
  },
  created() {
    this.ProductionPanelStageTaskStatusTypes = ProductionPanelStageTaskStatusTypes
  },
  mounted() {
  },
  computed: {
    taskRevertCount() {
      return this.taskList.reduce((curCount, task) => {
        return curCount + (this.isTaskToBeReverted(task) ? 1 : 0)
      }, 0)
    },
    isRevertAvailable() {
      if(this.productionPanelData == null)
        return false
      return !!this.revertPanelOptions.available
    },
    submitTitle() {
      let completeCount = this.taskRevertCount

      if(completeCount == 0)
        return 'Select Tasks'
      else if(completeCount == 1)
        return 'Revert 1 Task'
      return `Revert ${completeCount} Tasks`
    } 
  },
  methods: {
    show() {
      this.form = {}
      this.revertPanelOptions = { }
      this.taskList = []
      this.submitting = false

      this.requestPanelData()

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.confirmDeleteMessage('Revert Panel Tasks?', 'Are you sure you want to revert the selected tasks? They will need to be rescanned in again')
      .then(success => {
        if(success) {
          // Send revert request
          this.form = this.clearFormResponse()

          // Generate task completion
          var stageTaskIdList = this.taskList
          .filter(task => this.isTaskToBeReverted(task))
          .map(task => task.id)

          var revertData = {
            productionPanelId: this.productionPanelId,
            stageTaskIdList: stageTaskIdList
          }

          this.submitting = true
          this.$apiService.glassProductionStationService.revertPanelTasks(this.productionStationId, revertData)
          .then(response => {
            this.modalClosed(true)
            this.hide()
          })
          .catch(err => {
            this.form = this.processFormResponse(err)
          })
          .finally(() => {
            this.submitting = false
          })
        }
      })
    },

    requestPanelData() {
      this.loading = true
      this.loadingError = ''

      // Request panel & revert details
      Promise.all([
        this.$apiService.glassProductionStationService.getProductionPanel(this.productionPanelId),
        this.$apiService.glassProductionStationService.getPanelRevertTasks(this.productionStationId, this.productionPanelId)
      ])
      .then(results => {
        this.productionPanelData = results[0].data
        this.revertPanelOptions = results[1].data
        this.loading = false

        // Generate the task list for selection
        // Reverse the stage task list to revert back in last-completed order
        this.taskList = this.revertPanelOptions.panelStageTasks.reverse().map(task => {
          let taskInRevertStageList = this.revertPanelOptions.stageList.find(stage => stage.id == task.productionStageId)
          var completeFlag = false
          if(task.status == ProductionPanelStageTaskStatusTypes.completed)
            completeFlag = true

          return {
            id: task.id,
            status: task.status,
            dueDate: task.dueDate,
            completionDate: task.completionDate,
            productionStage: task.productionStage,
            taskOrder: task.taskOrder,
            selected: false,
            editable: taskInRevertStageList != null,
            completeFlag: completeFlag
          }
        })

        if(!this.revertPanelOptions.available)
          this.loadingError = this.revertPanelOptions.message
      })
      .catch(err => {
        this.loadingError = 'Error loading panel data'
      })
      .finally(() => {
        this.loading = false
      })
    },

    isTaskCurrent(stageTaskList, stageTask) {
      if(stageTask.status == ProductionPanelStageTaskStatusTypes.production)
        return true

      // If pending, and all tasks have been completed up to here, then this task is current
      // If no prior tasks, then this is the first & current task
      if(stageTask.status == ProductionPanelStageTaskStatusTypes.pending) {
        let previousTask = stageTaskList.find(taskItem => taskItem.taskOrder == stageTask.taskOrder-1)
        if(!previousTask || previousTask.status == ProductionPanelStageTaskStatusTypes.completed)
          return true
      }
      return false
    },
    isTaskToBeReverted(stageTask) {
      if(stageTask.status != ProductionPanelStageTaskStatusTypes.production && stageTask.status != ProductionPanelStageTaskStatusTypes.completed)
        return false

      return stageTask.selected
    },
    isTaskRevertedAhead(stageTask) {
      var taskRevertedAhead = false

      if(!this.isTaskToBeReverted(stageTask))
        return false

      this.taskList.forEach(stage => {
        if(stage.taskOrder > stageTask.taskOrder) {
          if(!stage.editable)
            return

          if(!this.isTaskToBeReverted(stage))
            taskRevertedAhead = true
        }
      })
      
      return taskRevertedAhead
    },
    tasksHaveErrors() {
      var errorCount = 0
      this.taskList.forEach(stage => {
        if(this.isTaskRevertedAhead(stage))
          errorCount++
      })
      return errorCount > 0
    }
  }
}
</script>